// WEB-01 — build the Showroom public-profile preview with DOM APIs, never an HTML string.
//
// The preview used to be a template literal handed to document.write, interpolating eleven
// tenant-controlled values — showroom name, description, contact phone/email, address, product
// name, description, price, category, and image/video URLs straight into src="" attributes. Any
// of those containing markup was parsed as HTML, and a crafted URL could close the attribute and
// open a new one. Reach was limited (a Business user previewing their own content, in their own
// window) but it was an unnecessary HTML injection sink.
//
// Here every tenant value goes in through textContent or setAttribute, so the browser treats it
// as data and never as markup. There is nothing to escape and no sanitiser to get wrong: the
// parser is simply never handed tenant input.
//
// Kept deliberately dependency-free so it is unit-testable under jsdom and carries no new
// rendering framework.

// Media URLs are assigned as attribute VALUES, which already prevents markup injection. This
// additionally refuses schemes that execute, so a stored "javascript:" URL cannot run if a future
// caller passes one to href/src.
const SAFE_MEDIA_SCHEMES = ['http:', 'https:', 'blob:', 'data:'];

export function isSafeMediaUrl(raw) {
  if (typeof raw !== 'string') return false;
  const v = raw.trim();
  if (!v) return false;
  // Relative paths and protocol-relative URLs carry no scheme and cannot execute.
  if (v.startsWith('/') || v.startsWith('./') || v.startsWith('../')) return true;
  try {
    // A base is required so relative inputs parse; absolute inputs ignore it.
    const u = new URL(v, 'https://placeholder.invalid/');
    return SAFE_MEDIA_SCHEMES.includes(u.protocol);
  } catch {
    return false;
  }
}

function el(doc, tag, { text, style, attrs } = {}) {
  const node = doc.createElement(tag);
  // textContent, never innerHTML: markup in a tenant value stays visible text.
  if (text !== undefined && text !== null) node.textContent = String(text);
  if (style) node.setAttribute('style', style);
  if (attrs) Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, String(v)));
  return node;
}

function labelled(doc, label, value) {
  const p = doc.createElement('p');
  p.appendChild(el(doc, 'strong', { text: label }));
  // A separate text node — the label and the tenant value can never merge into one parsed string.
  p.appendChild(doc.createTextNode(' ' + (value === undefined || value === null ? '' : String(value))));
  return p;
}

const STYLE = `
  body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; }
  h1 { color: #333; border-bottom: 2px solid #ddd; }
  .header { background: #f5f5f5; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
  .contact { background: #e8f4f8; padding: 15px; border-radius: 5px; }
`;

/**
 * Populate a preview document. `t` is the existing i18n lookup; `strings` are developer-authored.
 * Everything reached through `showroom` / `products` is tenant-authored and therefore only ever
 * becomes text or an attribute value.
 */
export function renderShowroomPreview(doc, { showroom, products, businessId, t }) {
  const tr = typeof t === 'function' ? t : (k) => k;

  doc.head.appendChild(el(doc, 'style', { text: STYLE }));
  // title is a property assignment, not markup.
  doc.title = `${showroom?.name || tr('fin.preview.showroomFallback')} - ${tr('fin.preview.publicProfilePreview')}`;

  const body = doc.body;

  const header = el(doc, 'div', { attrs: { class: 'header' } });
  header.appendChild(el(doc, 'h1', { text: showroom?.name || tr('fin.preview.businessShowroom') }));
  header.appendChild(labelled(doc, tr('fin.preview.businessId'), businessId));
  body.appendChild(header);

  body.appendChild(el(doc, 'h2', { text: tr('fin.preview.about') }));
  body.appendChild(el(doc, 'p', { text: showroom?.description || tr('fin.preview.noDescription') }));

  body.appendChild(el(doc, 'h2', { text: tr('fin.preview.contactInfo') }));
  const contact = el(doc, 'div', { attrs: { class: 'contact' } });
  contact.appendChild(labelled(doc, tr('fin.preview.email'),
    showroom?.contact_options?.email || tr('fin.preview.notProvided')));
  contact.appendChild(labelled(doc, tr('fin.preview.phone'),
    showroom?.contact_options?.phone || tr('fin.preview.notProvided')));
  contact.appendChild(labelled(doc, tr('fin.preview.location'),
    showroom?.location?.address || tr('fin.preview.notProvided')));
  contact.appendChild(labelled(doc, tr('fin.preview.chatAvailable'),
    showroom?.contact_options?.chat ? tr('common.yes') : tr('common.no')));
  contact.appendChild(labelled(doc, tr('fin.preview.inAppCalls'),
    showroom?.contact_options?.in_app_call ? tr('common.yes') : tr('common.no')));
  body.appendChild(contact);

  const list = Array.isArray(products) ? products : [];
  body.appendChild(el(doc, 'h2', {
    text: `${tr('fin.preview.productsServices')} (${list.length} ${tr('fin.preview.items')})`,
  }));

  const wrap = el(doc, 'div', { style: 'background: #f9f9f9; padding: 15px; border-radius: 5px;' });
  if (list.length > 0) {
    list.forEach((product, index) => {
      const card = el(doc, 'div', {
        style: 'border: 1px solid #ddd; padding: 10px; margin: 10px 0; border-radius: 3px; background: white;',
      });
      card.appendChild(el(doc, 'h3', {
        text: product?.name || `Product ${index + 1}`,
        style: 'color: #333; margin: 0 0 5px 0;',
      }));
      card.appendChild(el(doc, 'p', {
        text: product?.description || tr('fin.preview.noDescription'),
        style: 'margin: 5px 0; color: #666;',
      }));
      if (product?.pricing?.value) {
        card.appendChild(el(doc, 'p', {
          text: `${tr('fin.preview.price')} $${product.pricing.value}`,
          style: 'margin: 5px 0; font-weight: bold; color: #007bff;',
        }));
      }
      card.appendChild(el(doc, 'p', {
        text: `${tr('fin.preview.category')} ${product?.category_id || 'General'}`,
        style: 'margin: 5px 0; color: #888; font-size: 12px;',
      }));

      const images = Array.isArray(product?.images) ? product.images.filter(isSafeMediaUrl) : [];
      if (images.length > 0) {
        const strip = el(doc, 'div', { style: 'display:flex; flex-wrap:wrap; gap:6px; margin:8px 0;' });
        images.forEach((src) => {
          const img = el(doc, 'img', {
            attrs: { alt: 'product image' },
            style: 'width:96px; height:72px; object-fit:cover; border-radius:4px; border:1px solid #eee;',
          });
          img.setAttribute('src', src);   // attribute value, never interpolated into markup
          strip.appendChild(img);
        });
        card.appendChild(strip);
      } else {
        card.appendChild(el(doc, 'p', {
          text: tr('fin.preview.noImages'), style: 'color: #888; font-size: 12px;',
        }));
      }

      if (isSafeMediaUrl(product?.video)) {
        const holder = el(doc, 'div', { style: 'margin:8px 0;' });
        const video = el(doc, 'video', {
          attrs: { controls: '' },
          style: 'max-width:320px; max-height:220px; border-radius:4px;',
        });
        video.setAttribute('src', product.video);
        holder.appendChild(video);
        card.appendChild(holder);
      }
      wrap.appendChild(card);
    });
  } else {
    const empty = el(doc, 'div', { style: 'text-align: center; padding: 20px; color: #666;' });
    const p = doc.createElement('p');
    p.appendChild(el(doc, 'strong', { text: tr('fin.preview.noProducts') }));
    empty.appendChild(p);
    empty.appendChild(el(doc, 'p', { text: tr('fin.preview.productsWillAppear') }));
    // The old markup dumped JSON.stringify(products) into the page as "Debug Info". That leaked
    // the whole product array into rendered HTML and was a second unescaped sink; it is gone.
    wrap.appendChild(empty);
  }
  body.appendChild(wrap);

  body.appendChild(el(doc, 'hr', { style: 'margin: 30px 0;' }));
  body.appendChild(el(doc, 'p', { text: tr('fin.preview.footer'), style: 'color: #666; font-size: 14px;' }));

  return doc;
}
